import { Fragment, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { endpoints } from '@/api/endpoints';
import { useApi } from '@/api/useApi';
import { LoadingState } from '@/components/EmptyState';
import { ErrorBanner } from '@/components/ErrorBanner';
import { StatusBadge } from '@/components/StatusBadge';
import { EvidenceGallery } from '@/components/EvidenceGallery';
import { SkuMeta } from '@/components/SkuMeta';
import { useAuth } from '@/auth/AuthContext';
import { useLanguage } from '@/i18n/LanguageContext';
import { formatDateTime, formatNumber } from '@/i18n/format';
import { CancelOrderDialog } from './CancelOrderDialog';
import { LegacyShipmentRedirect } from '@/pages/Shipments/LegacyRedirect';
import { ApiError } from '@/api/client';
import { isInWork } from '@/lib/shipmentGroups';
import { RequestRevisionNotice } from '@/components/RequestRevisionNotice';
import type { CancelOrderResult, FulfilmentModel } from '@/api/types';
import type { TranslationKey } from '@/i18n/dictionaries/en';

/**
 * The unified "Shipments" card (`/shipments/:id`, also reached from the old `/orders/:id`): the order, its recipient and goods,
 * its statuses, the stock reserved for it and its pick tasks, plus its physical shipment(s) with tracking where they exist.
 * Everything is read from the existing order / shipment APIs - no second form or entity.
 *
 * Full lifecycle created→reserved→picking→picked→packing→packed→shipped,
 * plus lines and the audit timeline.
 *
 * REGULATED EVIDENCE epic (ADR-074) UI wiring: order-level evidence
 * (`entityKind: 'SALES_ORDER'`) - packing/quality photos captured against
 * the order as a whole rather than one specific inbound line.
 */
/** Orders that can still be cancelled from Ops (SHIPPED / CANCELLED cannot). */
const CANCELLABLE = new Set(['NEW', 'RESERVED', 'ON_HOLD', 'PICKING', 'PICKED', 'PACKED']);

/** The recipient fields an order can carry (the manual-order form and the seller portal write the same keys). */
const RECIPIENT_LABELS: Record<string, TranslationKey> = {
  name: 'orderCreate.recipientName',
  phone: 'orderCreate.phone',
  email: 'revNotice.f.shipTo.email',
  address: 'orderCreate.address',
  city: 'orderCreate.city',
  postalCode: 'orderCreate.postalCode',
  country: 'orderCreate.country',
};

export function OrderDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const result = useApi(() => endpoints.order(id!), [id]);
  const { hasPermission } = useAuth();
  const canReadEvidence = hasPermission('EVIDENCE.READ');
  const canWrite = hasPermission('ORDERS.WRITE');
  const { t, locale } = useLanguage();
  const [dialog, setDialog] = useState<'CANCEL' | 'FINISH' | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [decide, setDecide] = useState<'STANDARD' | 'FBS' | null>(null);
  const [deciding, setDeciding] = useState(false);
  const [decideError, setDecideError] = useState<unknown>(null);

  if (result.status === 'loading') return <LoadingState />;
  if (result.status === 'error') {
    // an old physical-shipment link: the id is not an order, but may be a shipment of one
    if (result.error instanceof ApiError && result.error.status === 404) return <LegacyShipmentRedirect id={id!} notFound={result.error} onRetry={result.reload} />;
    return <ErrorBanner error={result.error} onRetry={result.reload} />;
  }

  const { order, lines, activity, shipments = [], reservations = [], tasks = [] } = result.data;
  const header = order as Record<string, unknown>;
  const state = String(header.state);
  const isMarketplace = header.channel_name !== null && header.channel_name !== undefined;
  const canCancelState = CANCELLABLE.has(state);
  const fulfilment = (['STANDARD', 'FBS'].includes(String(header.fulfilment_model)) ? String(header.fulfilment_model) : 'UNKNOWN') as FulfilmentModel;
  const shipTo = (header.ship_to && typeof header.ship_to === 'object' ? header.ship_to : {}) as Record<string, unknown>;
  const recipient = (Object.keys(RECIPIENT_LABELS) as string[]).flatMap((k) => (typeof shipTo[k] === 'string' && String(shipTo[k]).trim() !== '' ? [[k, String(shipTo[k])] as const] : []));

  async function confirmDecision(): Promise<void> {
    if (!decide) return;
    setDeciding(true);
    setDecideError(null);
    try {
      await endpoints.decideFulfilmentModel(String(header.id), decide);
      setDecide(null);
      setNotice(t('shipments.card.decided'));
      result.reload();
    } catch (err) {
      setDecideError(err);
    } finally {
      setDeciding(false);
    }
  }

  function afterCancel(res: CancelOrderResult, finishing: boolean): void {
    setDialog(null);
    setNotice(
      finishing
        ? t('ordCancel.doneFinished')
        : res.state === 'CANCELLED_AFTER_PICK'
          ? t('ordCancel.doneAfterPick')
          : t('ordCancel.doneCancelled', { n: res.reservationsReleased }),
    );
    result.reload();
  }

  return (
    <div>
      <div className="page-header">
        <h1>{t('orderDetail.title', { number: String(header.number) })}</h1>
        <StatusBadge value={state} />
        {isInWork(state) && <span className="badge">{t('shipments.inWork')}</span>}
        {canWrite && canCancelState && !dialog && !isMarketplace && (
          <button type="button" className="danger" onClick={() => setDialog('CANCEL')}>
            {t('ordCancel.button')}
          </button>
        )}
      </div>

      {notice && (
        <div className="success-card card" role="status">
          {notice}
        </div>
      )}

      {canWrite && canCancelState && isMarketplace && <p className="field-hint">{t('ordCancel.marketplace')}</p>}

      <RequestRevisionNotice kind="OUTBOUND" revisionNo={header.revision_no as number | undefined} revisions={result.data.revisions} />

      {state === 'CANCELLED_AFTER_PICK' && (
        <div className="card danger-card" role="alert" data-testid="after-pick-banner">
          <h2 className="card-title">{t('ordCancel.bannerTitle')}</h2>
          <p>{t('ordCancel.bannerText')}</p>
          {canWrite && (
            <div className="form-actions">
              <Link className="button-link" to="/inventory">
                {t('ordCancel.goMove')}
              </Link>
              {dialog !== 'FINISH' && (
                <button type="button" className="primary" onClick={() => setDialog('FINISH')}>
                  {t('ordCancel.finish')}
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {dialog && (
        <CancelOrderDialog
          orderId={String(header.id)}
          number={String(header.number)}
          state={state}
          mode={dialog}
          onClose={() => setDialog(null)}
          onDone={(res) => afterCancel(res, dialog === 'FINISH')}
        />
      )}

      <div className="card">
        <dl className="kv">
          <dt>{t('inbounds.colSeller')}</dt>
          <dd>{String(header.owner_name)}</dd>
          <dt>{t('queue.colChannel')}</dt>
          <dd>{header.channel_name ? String(header.channel_name) : header.external_source === 'MANUAL' ? t('orderCreate.manualSource') : header.external_source === 'SELLER_PORTAL' ? t('orderCreate.sellerPortalSource') : '—'}</dd>
          <dt>{t('inbounds.colWarehouse')}</dt>
          <dd>{String(header.warehouse_name)}</dd>
          <dt>{t('orders.colDue')}</dt>
          <dd>{header.due_at ? formatDateTime(String(header.due_at), locale) : '—'}</dd>
          <dt>{t('shipments.card.fulfilment')}</dt>
          <dd><span className={`badge${fulfilment === 'UNKNOWN' ? ' warn' : ''}`}>{t(`fulfilment.${fulfilment}`)}</span></dd>
        </dl>
        {fulfilment === 'UNKNOWN' && (
          <div style={{ marginTop: 12 }}>
            <p className="field-hint">{t('shipments.card.unknownHint')}</p>
            {canWrite && !decide && (
              <div className="form-actions">
                <button type="button" onClick={() => setDecide('STANDARD')}>{t('shipments.card.setStandard')}</button>
                <button type="button" onClick={() => setDecide('FBS')}>{t('shipments.card.setFbs')}</button>
              </div>
            )}
            {canWrite && decide && (
              <div role="group" aria-label={t('shipments.card.fulfilment')}>
                <p>{t('shipments.card.decideConfirm', { model: t(`fulfilment.${decide}`), number: String(header.number) })}</p>
                {decideError instanceof ApiError && decideError.code === 'ORDER.FULFILMENT_ALREADY_SET' ? (
                  <div className="error-banner" role="alert">{t('shipments.card.alreadySet')}</div>
                ) : (
                  Boolean(decideError) && <ErrorBanner error={decideError} />
                )}
                <div className="form-actions">
                  <button type="button" className="primary" disabled={deciding} onClick={() => void confirmDecision()}>{t('common.confirm')}</button>
                  <button type="button" disabled={deciding} onClick={() => { setDecide(null); setDecideError(null); }}>{t('common.cancel')}</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="card">
        <h2 style={{ fontSize: 14, marginTop: 0 }}>{t('shipments.card.recipient')}</h2>
        {recipient.length === 0 ? (
          <p className="sub" style={{ margin: 0 }}>{t('shipments.card.noRecipient')}</p>
        ) : (
          <dl className="kv">
            {recipient.map(([k, v]) => (
              <Fragment key={k}>
                <dt>{t(RECIPIENT_LABELS[k]!)}</dt>
                <dd>{v}</dd>
              </Fragment>
            ))}
          </dl>
        )}
      </div>

      <div className="card">
        <h2 style={{ fontSize: 14, marginTop: 0 }}>{t('shipments.card.physical')}</h2>
        {shipments.length === 0 ? (
          <p className="sub" style={{ margin: 0 }}>{t('shipments.card.noPhysical')}</p>
        ) : (
          <div className="table-scroll"><table>
            <thead>
              <tr>
                <th>{t('shipments.colNumber')}</th>
                <th>{t('common.status')}</th>
                <th>{t('shipments.colPackages')}</th>
                <th>{t('shipments.colCarrier')}</th>
                <th>{t('shipments.colShippedAt')}</th>
                <th><span className="sr-only">{t('common.actions')}</span></th>
              </tr>
            </thead>
            <tbody>
              {shipments.map((sh) => (
                <tr key={sh.id}>
                  <td>{sh.number}</td>
                  <td><StatusBadge value={sh.state} /></td>
                  <td>{formatNumber(sh.package_count, locale)}</td>
                  <td>{[sh.carrier_kind, sh.carrier_ref].filter(Boolean).join(' · ') || '—'}</td>
                  <td>{sh.departed_at ? formatDateTime(sh.departed_at, locale) : '—'}</td>
                  <td><Link to={`/shipments/doc/${sh.id}`}>{t('shipments.card.openDocument')}</Link></td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>

      <div className="card">
        <h2 style={{ fontSize: 14, marginTop: 0 }}>{t('inboundDetail.lines')}</h2>
        {lines.length === 0 ? (
          <p>{t('orderDetail.noLines')}</p>
        ) : (
          <div className="table-scroll"><table>
            <thead>
              <tr>
                <th>{t('inventory.colSku')}</th>
                <th>{t('orderDetail.colOrdered')}</th>
                <th>{t('orders.colReserved')}</th>
                <th>{t('orders.colPicked')}</th>
                <th>{t('orders.colPacked')}</th>
                <th>{t('orderDetail.colShipped')}</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((row, i) => {
                const r = row as Record<string, unknown>;
                const lineCancelled = Boolean(r.cancelled_at);
                return (
                  <tr key={i} style={lineCancelled ? { textDecoration: 'line-through', opacity: 0.7 } : undefined}>
                    <td>
                      {String(r.sku_code)} — {String(r.sku_name)}
                      <SkuMeta
                        code={String(r.sku_code)}
                        article={r.article as string | undefined}
                        size={r.size as string | undefined}
                        color={r.color as string | undefined}
                        barcodes={r.barcodes as readonly string[] | undefined}
                      />
                      {lineCancelled && (
                        <span className="badge danger" style={{ marginLeft: 8, textDecoration: 'none', display: 'inline-flex' }}>
                          {t('ordCancel.lineCancelled')}
                        </span>
                      )}
                    </td>
                    <td>{formatNumber(String(r.qty_ordered), locale)}</td>
                    <td>{formatNumber(String(r.qty_reserved), locale)}</td>
                    <td>{formatNumber(String(r.qty_picked), locale)}</td>
                    <td>{formatNumber(String(r.qty_packed), locale)}</td>
                    <td>{formatNumber(String(r.qty_shipped), locale)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table></div>
        )}
      </div>

      <div className="card">
        <h2 style={{ fontSize: 14, marginTop: 0 }}>{t('shipments.card.reservations')}</h2>
        {reservations.length === 0 ? (
          <p className="sub" style={{ margin: 0 }}>{t('shipments.card.noReservations')}</p>
        ) : (
          <div className="table-scroll"><table>
            <thead>
              <tr>
                <th>{t('inventory.colSku')}</th>
                <th>{t('shipments.card.colCell')}</th>
                <th>{t('shipments.card.colQty')}</th>
                <th>{t('common.status')}</th>
              </tr>
            </thead>
            <tbody>
              {reservations.map((r) => (
                <tr key={r.id}>
                  <td>{r.sku_code}</td>
                  <td>{r.location_code}</td>
                  <td>{formatNumber(r.qty, locale)}</td>
                  <td><StatusBadge value={r.state} /></td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>

      <div className="card">
        <h2 style={{ fontSize: 14, marginTop: 0 }}>{t('shipments.card.tasks')}</h2>
        {tasks.length === 0 ? (
          <p className="sub" style={{ margin: 0 }}>{t('shipments.card.noTasks')}</p>
        ) : (
          <div className="table-scroll"><table>
            <thead>
              <tr>
                <th>{t('inventory.colSku')}</th>
                <th>{t('shipments.card.colCell')}</th>
                <th>{t('shipments.card.colDone')}</th>
                <th>{t('common.status')}</th>
              </tr>
            </thead>
            <tbody>
              {tasks.map((k) => (
                <tr key={k.id}>
                  <td>{k.sku_code}</td>
                  <td>{k.location_code ?? '—'}</td>
                  <td>{formatNumber(k.qty_done, locale)} / {k.qty_target === null ? '—' : formatNumber(k.qty_target, locale)}</td>
                  <td><StatusBadge value={k.state} /></td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>

      <div className="card">
        <h2 style={{ fontSize: 14, marginTop: 0 }}>{t('inboundDetail.evidence')}</h2>
        <EvidenceGallery
          entityKind="SALES_ORDER"
          entityId={String(header.id)}
          ownerId={String(header.owner_id)}
          canRead={canReadEvidence}
        />
      </div>

      <div className="card">
        <h2 style={{ fontSize: 14, marginTop: 0 }}>{t('orderDetail.activityTimeline')}</h2>
        {activity.length === 0 ? (
          <p>{t('orderDetail.noActivity')}</p>
        ) : (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {activity.map((row, i) => {
              const r = row as Record<string, unknown>;
              return (
                <li key={i} style={{ padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                  <strong>{String(r.action)}</strong>{' '}
                  <span className="sub">{formatDateTime(String(r.occurred_at), locale)}</span>
                  <div className="sub">{t('orderDetail.byActor', { actor: String(r.actor_kind) })}</div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
